import { createSelector, createSlice } from '@reduxjs/toolkit'
import type {
  PastPerformanceResults,
  PastPerformancePlayerData,
  PlayerPastEvents,
  PlayerProp,
  SDKWeek,
  NFLWeekAbbreviation,
  InjuryData,
  FootballGameV2,
  NewsItem,
  FantasyWeekProjection,
  PlayerCareer,
  FootballPlayerWithGradesTeamAndDraftInfo,
  FootballPlayerActiveSeason,
} from '@pff-consumer/schema'
import { PlayerPosition } from '@pro-football-focus/pffkit-js-core'
import {
  PerformanceMetricStat,
  HistoricalPerformanceToggleOptions,
  HistoricalPerformanceMetrics,
  GameScript,
} from '@pff-consumer/schema'
import {
  PerformanceMetricStatKeyToUnit,
  PerformanceMetricToStatKey,
  getSeasonForGrades,
  getPlayerInjuryStatus,
} from '@pff-consumer/utils'
import { HYDRATE } from 'next-redux-wrapper'
import { RankingsEntity } from '../fantasy/fantasy.slice'
import { createDeepEqualSelector } from '../createDeepEqualSelector'
import { getSelectedMetric } from './utils/get-selected-metric'
import { getDefaultActiveSeason } from './utils/get-default-active-season'
import type { PlayerProfilesState } from './player-profile-types'

export const PLAYER_PROFILE_KEY = 'player-profile'

export const initialState: PlayerProfilesState = {
  league: 'nfl',
  isInSeason: false,
  selectedPlayer: null,
  currentGameWeek: null,
  playerProfileHistoricalPerformanceResults: {},
  historicalPerformance: {
    selectedStat: PerformanceMetricStat.ANY_TIME_TD,
    selectedToggle: HistoricalPerformanceToggleOptions.AvgL5,
  },
  fantasyRankings: null,
  isMatchupsDataAvailable: false,
  matchupOverallScore: 0,
  injuryData: null,
  activeSeasons: [],
  gameScripts: null,
  nextGame: null,
  latestNews: [],
  weekProjections: null,
  career: null,
  pageUpdatedAt: '',
}

export const playerProfileSlice = createSlice({
  name: PLAYER_PROFILE_KEY,
  initialState,
  extraReducers: {
    [HYDRATE]: (state, action) => {
      return {
        ...state,
        ...action.payload[PLAYER_PROFILE_KEY],
      }
    },
  },
  reducers: {
    updateSelectedPlayer: (state, { payload }: { payload: FootballPlayerWithGradesTeamAndDraftInfo }) => {
      state.selectedPlayer = payload
    },
    updateCurrentGameWeek: (state, { payload }: { payload: SDKWeek }) => {
      const { abbreviation, status } = payload
      state.isInSeason = !(abbreviation === '1' && status === 'upcoming')
      state.currentGameWeek = payload
    },
    updatePlayerProfileHistoricalPerformanceResults: (state, { payload }: { payload: PastPerformanceResults }) => {
      state.playerProfileHistoricalPerformanceResults = payload
    },
    updateHistoricalPerformanceSelectedStat: (state, { payload }: { payload: PerformanceMetricStat }) => {
      state.historicalPerformance.selectedStat = payload
    },
    updateHistoricalPerformanceSelectedToggle: (
      state,
      { payload }: { payload: HistoricalPerformanceToggleOptions }
    ) => {
      state.historicalPerformance.selectedToggle = payload
    },
    updateFantasyRankings: (state, { payload }: { payload: { players: RankingsEntity[] } }) => {
      state.fantasyRankings = payload
    },
    updateIsMatchupsDataAvailable: (state, { payload }: { payload: boolean }) => {
      state.isMatchupsDataAvailable = payload
    },
    updateMatchupOverallScore: (state, { payload }: { payload: number }) => {
      state.matchupOverallScore = payload
    },
    updateInjuryData: (state, { payload }: { payload: InjuryData[] }) => {
      state.injuryData = payload
    },
    updatePlayerActiveSeasons: (state, { payload }: { payload: FootballPlayerActiveSeason[] }) => {
      state.activeSeasons = payload
    },
    updateGameScripts: (
      state,
      {
        payload,
      }: {
        payload: {
          season: number
          week: NFLWeekAbbreviation
          scripts: GameScript[]
        }
      }
    ) => {
      state.gameScripts = payload
    },
    updateNextGame: (state, { payload }: { payload: FootballGameV2 }) => {
      state.nextGame = payload
    },
    updateLatestNews: (state, { payload }: { payload: NewsItem[] }) => {
      state.latestNews = payload
    },
    updateWeekProjections: (state, { payload }: { payload: FantasyWeekProjection }) => {
      state.weekProjections = payload
    },
    updateCareer: (state, { payload }: { payload: PlayerCareer }) => {
      state.career = payload
    },
    setPageUpdatedAt: (state, { payload }: { payload: { pageUpdatedAt: string } }) => {
      state.pageUpdatedAt = payload.pageUpdatedAt
    },
    setLeague: (state, { payload }: { payload: string }) => {
      state.league = payload
    },
  },
})

export const getPlayerProfilesState = (rootState: { [x: string]: PlayerProfilesState }): PlayerProfilesState => {
  return rootState[PLAYER_PROFILE_KEY]
}

export const playerProfilesSelectors = {
  getSelectedPlayer: createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.selectedPlayer
  }),
  getCurrentGameWeek: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.currentGameWeek?.abbreviation
  }),
  getCurrentSeason: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.currentGameWeek?.season
  }),
  getSelectedPlayerId: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.selectedPlayer?.playerId || ''
  }),
  getSelectedPlayerPosition: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.activeSeasons?.at(0)?.position ?? state.selectedPlayer?.position ?? ''
  }),
  getSelectedPlayerTeam: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const playerTeam = state.activeSeasons?.at(0)?.team
    if (!playerTeam) {
      return state.selectedPlayer?.team ?? null
    }
    return { ...playerTeam }
  }),
  getPlayerPastPerformance: createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const playerId = state.selectedPlayer?.playerId
    const playerMetric = getSelectedMetric(state)
    const data = state.playerProfileHistoricalPerformanceResults[playerMetric]
    // explicitly return undefined to satisfy eslint: consistent-return
    if (!data || !playerId) return undefined
    const event = data.events[0]
    const pastPerformance = event?.players?.find((p) => p.playerId === playerId)
    return pastPerformance
  }),
  getHasHistoricalPerformanceData: createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const playerId = state.selectedPlayer?.playerId
    const pastPerformanceData =
      state.playerProfileHistoricalPerformanceResults[HistoricalPerformanceMetrics.PAST_PERFORMANCE]
    const similarDefenseData =
      state.playerProfileHistoricalPerformanceResults[HistoricalPerformanceMetrics.SIMILAR_DEFENSE]
    if (!pastPerformanceData && !similarDefenseData) {
      return false
    }
    const pastPerformance = pastPerformanceData?.events[0]?.players?.find((p) => p.playerId === playerId)
    const similarDefense = similarDefenseData?.events[0]?.players?.find((p) => p.playerId === playerId)
    return !!(pastPerformance || similarDefense)
  }),

  getPlayerHistoricalStatsSummary: (player: PastPerformancePlayerData) =>
    createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
      const playerProps = state.playerProfileHistoricalPerformanceResults.playerPropData?.playerProps || []
      const statKey = state.historicalPerformance.selectedStat
      // Find the playerProp/betLine for passed in playerid and statKey
      const betLineValue = playerProps.find(
        (playerProp: PlayerProp) => playerProp.playerId === player?.playerId && playerProp.propKey === statKey
      )?.value

      if (!betLineValue) return { playerSummary: '', isOver: false, hasValidData: false }

      // Get the player's historical performances for passed in statkey
      const historicalPerformances = player.pastEvents.map((pastEvent: PlayerPastEvents) =>
        pastEvent.performances.find((performance) => performance.statKey === statKey)
      )
      const historicalPerformancesCount = historicalPerformances.length

      const performancesOverBetLine = historicalPerformances.filter(
        (performance) => performance?.statValue && performance.statValue >= Number(betLineValue)
      )
      const performancesOverBetLineCount = performancesOverBetLine.length

      const overBetPercentage = Math.round((performancesOverBetLineCount / historicalPerformancesCount) * 100)
      const betLineWithUnit = `${betLineValue} ${PerformanceMetricStatKeyToUnit[statKey]}`
      const playerSummary = `Has hit over ${betLineWithUnit} ${performancesOverBetLineCount}/${historicalPerformancesCount} games ${overBetPercentage}%`
      const isOver = overBetPercentage > 50

      return { playerSummary, isOver, hasValidData: playerSummary !== '' }
    }),
  getHistoricalPerformanceSelectedStat: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.historicalPerformance.selectedStat
  }),
  getHistoricalPerformanceSelectedStatName: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return PerformanceMetricToStatKey[state.historicalPerformance.selectedStat]
  }),
  getHistoricalPerformanceBetLine: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const statKey = state.historicalPerformance.selectedStat
    const playerId = state.selectedPlayer?.playerId
    const playerProps = state.playerProfileHistoricalPerformanceResults.playerPropData?.playerProps || []
    // Find the playerProp/betLine for passed in playerid and statKey
    const betLineValue =
      (playerProps?.find((playerProp: PlayerProp) => playerProp.playerId === playerId && playerProp.propKey === statKey)
        ?.value as number) || 0

    return betLineValue
  }),
  getHistoricalPerformanceSelectedToggle: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.historicalPerformance.selectedToggle
  }),
  getHistoricalPerformanceSelectedMetric: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const position = state.selectedPlayer?.position
    const similarMetric = position === 'WR' ? 'similar coverage' : 'similar defence'
    return state.historicalPerformance.selectedToggle === HistoricalPerformanceToggleOptions.AvgL5
      ? 'past performance'
      : similarMetric
  }),
  getFantasyRankings: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.fantasyRankings?.players
  }),
  getIsInSeason: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    if (!state.currentGameWeek) {
      return false
    }
    const { abbreviation, status } = state.currentGameWeek
    return !(abbreviation === '1' && status === 'upcoming')
  }),
  getPlayerInjuryStatus: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const currentSeason = state.currentGameWeek?.season
    const lastActiveSeason = state.activeSeasons?.at(0)?.season
    const isActive = currentSeason === lastActiveSeason

    const selectedPlayerId = state.selectedPlayer?.playerId
    const events = state.injuryData

    if (!selectedPlayerId || !events) {
      return ''
    }

    const status = getPlayerInjuryStatus({ events, selectedPlayerId, isActive })

    return status
  }),
  getActiveSeasons: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const isInSeason = !(state.currentGameWeek?.abbreviation === '1' && state.currentGameWeek?.status === 'upcoming')
    const seasons = state.activeSeasons.filter(
      (season: FootballPlayerActiveSeason) => season.league === state.league?.toUpperCase()
    )
    return isInSeason ? seasons : seasons?.slice(1)
  }),
  getIsPlayerActive: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const currentSeason = state.currentGameWeek?.season
    const lastActiveSeason = state.activeSeasons?.at(0)?.season
    return currentSeason === lastActiveSeason
  }),
  getPlayerTeamForLeague: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const playerTeam = state.activeSeasons?.find(
      (season: FootballPlayerActiveSeason) => season.league.toUpperCase() === state.league?.toUpperCase()
    )?.team
    return playerTeam
  }),
  getIsMatchupsDataAvailable: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.isMatchupsDataAvailable
  }),
  getIsMatchupsVisible: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const position = state?.selectedPlayer?.position
    const isMatchupsVisible = [PlayerPosition.WideReceiver, PlayerPosition.TightEnd].includes(
      position as PlayerPosition
    )
    return isMatchupsVisible
  }),
  getMatchupsOverallScore: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.matchupOverallScore
  }),
  getGameScripts: createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state?.gameScripts || null
  }),
  getNextGame: createDeepEqualSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state?.nextGame || null
  }),
  getIsByeWeek: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const isRegularSeason = state.currentGameWeek?.weekType === 'REG'
    const nextGame = state?.nextGame
    const isByeWeek = !nextGame && isRegularSeason
    return isByeWeek
  }),
  getDefaultActiveSeason: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return getDefaultActiveSeason(state.currentGameWeek, state.activeSeasons, state.league!)
  }),
  getIsFantasyInsightsDataAvailable: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const player = state.selectedPlayer
    const rankings = state.fantasyRankings?.players?.[0]

    return !!(player?.team && (rankings?.rank?.current || rankings?.projection?.points?.mid || rankings?.adp))
  }),
  getIsHistoricalPerformanceApplicable: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const position = state.selectedPlayer?.position
    const allowedPositions = ['QB', 'RB', 'HB', 'FB', 'WR', 'TE']
    const currentSeason = state.currentGameWeek?.season
    if (currentSeason) {
      const previousSeason = currentSeason - 1
      const lastTwoSeasons = state.activeSeasons.filter(
        (season: FootballPlayerActiveSeason) => season.season === currentSeason || season.season === previousSeason
      )
      if (lastTwoSeasons.length === 0) {
        // don't show historical performance if player hasn't played last two seasons
        return false
      }
    }

    return position && allowedPositions.indexOf(position) !== -1
  }),

  getHeaderPremiumStatsUrl: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    const { isInSeason, league } = state
    const playerId = state.selectedPlayer?.playerId || ''
    const currentSeason = state.currentGameWeek?.season
    const gradesSeason = getSeasonForGrades(currentSeason, isInSeason)
    return `https://premium.pff.com/${league}/players/${gradesSeason}/REGPO/player/${playerId}`
  }),

  getLatestNews: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.latestNews
  }),
  getIsLatestNewsAvailable: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.latestNews.length > 0
  }),
  getWeekProjections: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.weekProjections
  }),
  getIsWeekProjectionsAvailable: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.weekProjections !== null
  }),
  getHasCareerData: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.career && (state.career.teams?.length > 0 || state.career.honors?.length > 0)
  }),
  getPageUpdatedAt: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.pageUpdatedAt
  }),
  getLeague: createSelector(getPlayerProfilesState, (state: PlayerProfilesState) => {
    return state.league
  }),
}

export const playerProfileActions = playerProfileSlice.actions
